import type { IzidVabila, Podjetje, PregledUporabnikov, UporabnikZaPrikaz } from '@koda/shared';
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { datum, datumInUra } from '../lib/format';

const STATUS: Record<UporabnikZaPrikaz['statusVabila'], string> = {
  sprejeto: 'dostop urejen',
  poslano: 'vabilo poslano, še ni sprejeto',
  'brez-vabila': 'brez vabila',
};

/** Izid vabila ali ponastavitve gesla, ki ga skrbnik vidi po kliku. */
interface Izid {
  vrsta: 'vabilo' | 'ponastavitev';
  odgovor: IzidVabila;
}

/**
 * Celotna povezava do vabila, kot jo je sestavil strežnik.
 *
 * Naslova **ne** sestavljamo iz naslova v brskalniku: skrbnik ima portal
 * lahko odprt na starem naslovu ali na različici z `www.`, stranka pa bi
 * potem dobila povezavo, ki ne vodi tja, kamor mislimo. Edini vir je
 * nastavitev `PORTAL_URL` na strežniku; če je ni, povezave ni in to je treba
 * povedati, ne pa ugibati.
 */
function celotnaPovezava(izid: IzidVabila): string | null {
  return izid.povezava;
}

/**
 * Uporabniki in dostopi (samo skrbnik).
 *
 * Vabilo se pošlje po e-pošti; če pošiljanje ni nastavljeno ali ne uspe, se
 * tukaj izpiše povezava, ki jo skrbnik posreduje sam. Neuspelo pošiljanje je
 * vidno tudi pri uporabniku, da ne ostane neopaženo.
 */
export function Uporabniki(): JSX.Element {
  const [pregled, setPregled] = useState<PregledUporabnikov | null>(null);
  const [podjetja, setPodjetja] = useState<Podjetje[]>([]);
  const [email, setEmail] = useState('');
  const [ime, setIme] = useState('');
  const [izbrana, setIzbrana] = useState<Record<string, string>>({});
  const [posiljam, setPosiljam] = useState(false);
  /** Uporabnik, ki mu se pošiljamo povezavo za novo geslo (gumb je takrat zaklenjen). */
  const [ponastavljam, setPonastavljam] = useState<string | null>(null);
  const [izid, setIzid] = useState<Izid | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  const osvezi = (): void => {
    api
      .uporabniki()
      .then(setPregled)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Uporabnikov ni bilo mogoče prebrati.');
      });
  };

  useEffect(() => {
    osvezi();
    api.podjetja().then(setPodjetja).catch(() => {
      setPodjetja([]);
    });
  }, []);

  const poslji = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    setPosiljam(true);
    setNapaka(null);
    setIzid(null);
    api
      .povabi(
        email,
        ime,
        Object.entries(izbrana).map(([id, vloga]) => ({ id, vloga })),
      )
      .then((odgovor) => {
        setIzid({ vrsta: 'vabilo', odgovor });
        setEmail('');
        setIme('');
        setIzbrana({});
        osvezi();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Vabila ni bilo mogoče poslati.');
      })
      .finally(() => {
        setPosiljam(false);
      });
  };

  /** Arhiviranje umakne uporabnika s seznama; obnovi se ga v arhivu. */
  const arhiviraj = (uporabnik: UporabnikZaPrikaz): void => {
    setNapaka(null);
    api
      .arhivirajUporabnika(uporabnik.id)
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Uporabnika ni bilo mogoče arhivirati.');
      });
  };

  /**
   * Dodelitev in odvzem podjetja obstoječemu uporabniku, brez novega vabila.
   * Odvzem velja takoj; zato ga potrdimo, da ga ne sproži zgrešen klik.
   */
  const dodeli = (uporabnik: UporabnikZaPrikaz, podjetje: string): void => {
    setNapaka(null);
    api
      .dodeliPodjetje(uporabnik.id, podjetje)
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Podjetja ni bilo mogoče dodeliti.');
      });
  };

  const odvzemi = (uporabnik: UporabnikZaPrikaz, podjetje: { id: string; naziv: string }): void => {
    if (!window.confirm(`${uporabnik.email} odvzamem podjetje ${podjetje.naziv}? Velja takoj.`)) return;
    setNapaka(null);
    api
      .odvzemiPodjetje(uporabnik.id, podjetje.id)
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Podjetja ni bilo mogoče odvzeti.');
      });
  };

  /**
   * Ponastavitev gesla: uporabnik dobi povezavo in si geslo nastavi sam;
   * skrbnik ga ne vidi. Staro geslo velja, dokler novega ne nastavi.
   */
  const ponastaviGeslo = (uporabnik: UporabnikZaPrikaz): void => {
    // Dvojni klik ne sme poslati dveh sporočil (drugo bi preklicalo prvo).
    if (ponastavljam !== null) return;
    if (
      !window.confirm(
        `${uporabnik.email} pošljem povezavo za novo geslo? Velja eno uro. Staro geslo velja, dokler si novega ne nastavi.`,
      )
    ) {
      return;
    }
    setNapaka(null);
    setIzid(null);
    setPonastavljam(uporabnik.id);
    api
      .ponastaviGeslo(uporabnik.id)
      .then((odgovor) => {
        setIzid({ vrsta: 'ponastavitev', odgovor });
        osvezi();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Povezave za novo geslo ni bilo mogoče poslati.');
      })
      .finally(() => {
        setPonastavljam(null);
      });
  };

  const koncajSeje = (uporabnik: UporabnikZaPrikaz): void => {
    api
      .koncajVseSeje(uporabnik.id)
      .then(({ koncanihSej }) => {
        setNapaka(null);
        setIzid(null);
        window.alert(
          koncanihSej === 0
            ? `${uporabnik.email} ni imel odprtih sej.`
            : `Končanih sej: ${String(koncanihSej)}. ${uporabnik.email} se mora prijaviti znova.`,
        );
        osvezi();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Sej ni bilo mogoče končati.');
      });
  };

  return (
    <main>
      <h1>Uporabniki</h1>

      {napaka !== null && <p className="napaka">{napaka}</p>}

      {pregled !== null && !pregled.postaNastavljena && (
        <p className="opozorilo">
          Pošiljanje e-pošte ni nastavljeno (spremenljivke <code>GRAPH_*</code> in{' '}
          <code>PORTAL_URL</code>). Vabila zato ne odidejo sama — po vabilu se tu izpiše povezava,
          ki jo pošljete stranki sami. Podrobnosti so na strani <Link to="/posta">Pošiljanja</Link>.
        </p>
      )}

      <section className="kartica">
        <h2>Novo vabilo</h2>
        <form onSubmit={poslji} className="obrazec">
          <label>
            E-pošta
            <input
              type="email"
              value={email}
              onChange={(d) => {
                setEmail(d.target.value);
              }}
              required
            />
          </label>
          <label>
            Ime (ni obvezno)
            <input
              value={ime}
              onChange={(d) => {
                setIme(d.target.value);
              }}
              placeholder="npr. Janez Novak"
            />
          </label>
          {podjetja.length > 0 && (
            <fieldset className="podjetja-izbira">
              <legend>Dostop do podjetij</legend>
              {podjetja.map((p) => (
                <label key={p.id} className="vrstica-izbire">
                  <input
                    type="checkbox"
                    checked={izbrana[p.id] !== undefined}
                    onChange={(d) => {
                      setIzbrana((prej) => {
                        const naslednje = { ...prej };
                        if (d.target.checked) naslednje[p.id] = 'viewer';
                        else delete naslednje[p.id];
                        return naslednje;
                      });
                    }}
                  />
                  <span>{p.naziv}</span>
                  {izbrana[p.id] !== undefined && (
                    <select
                      value={izbrana[p.id]}
                      onChange={(d) => {
                        setIzbrana((prej) => ({ ...prej, [p.id]: d.target.value }));
                      }}
                    >
                      <option value="viewer">pregled</option>
                      <option value="editor">urejanje razporeditve</option>
                    </select>
                  )}
                </label>
              ))}
            </fieldset>
          )}
          <button type="submit" disabled={posiljam || email.trim() === ''}>
            {posiljam ? 'Pošiljam …' : 'Pošlji vabilo'}
          </button>
        </form>

        {izid !== null && izid.vrsta === 'vabilo' && <IzidPovezave izid={izid.odgovor} vrsta="vabilo" />}
      </section>

      <section className="kartica">
        <h2>Pregled uporabnikov</h2>
        {izid !== null && izid.vrsta === 'ponastavitev' && <IzidPovezave izid={izid.odgovor} vrsta="ponastavitev" />}
        {pregled === null && <p>Berem …</p>}
        {pregled !== null && (
          <ul className="seznam uporabniki">
            {pregled.uporabniki.map((u) => (
              <li key={u.id}>
                <div>
                  <div>
                    {u.ime ?? u.email}
                    {u.jeAdmin && <span className="oznaka">skrbnik</span>}
                  </div>
                  <div className="drobno">{u.email}</div>
                  {u.jeAdmin ? (
                    <div className="drobno">vsa podjetja</div>
                  ) : (
                    <div className="drobno podjetja-uporabnika">
                      {u.podjetja.length === 0 && <span>brez podjetij</span>}
                      {u.podjetja.map((p) => (
                        <span key={p.id} className="podjetje-uporabnika">
                          {p.naziv} ({p.vloga}){' '}
                          <button
                            type="button"
                            className="povezava nevarna"
                            aria-label={`Odvzemi ${p.naziv}`}
                            onClick={() => {
                              odvzemi(u, p);
                            }}
                          >
                            odvzemi
                          </button>
                        </span>
                      ))}
                      {podjetja.some((p) => !u.podjetja.some((x) => x.id === p.id)) && (
                        <select
                          aria-label={`Dodeli podjetje uporabniku ${u.email}`}
                          value=""
                          onChange={(d) => {
                            if (d.target.value !== '') dodeli(u, d.target.value);
                          }}
                        >
                          <option value="">+ dodeli podjetje …</option>
                          {podjetja
                            .filter((p) => !u.podjetja.some((x) => x.id === p.id))
                            .map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.naziv}
                              </option>
                            ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div className="drobno">
                    {STATUS[u.statusVabila]}
                    {u.vabiloVelja !== null && ` — velja do ${datum(u.vabiloVelja)}`}
                    {' · '}
                    {u.zadnjaPrijava === null
                      ? 'še brez prijave'
                      : `zadnja prijava ${datum(u.zadnjaPrijava)}`}
                  </div>
                  {u.zadnjaNapakaPoste !== null && (
                    <div className="napaka drobno">
                      Zadnje pošiljanje ni uspelo ({datum(u.zadnjaNapakaPoste.ob)}) —{' '}
                      <Link
                        to={`/posta?uporabnik=${u.id}&zapis=${String(u.zadnjaNapakaPoste.id)}`}
                      >
                        podrobnosti
                      </Link>
                    </div>
                  )}
                </div>
                <div className="dnevnik-gumbi">
                  {u.statusVabila === 'sprejeto' && (
                    <button
                      type="button"
                      className="povezava"
                      aria-label={`Ponastavi geslo ${u.email}`}
                      disabled={ponastavljam !== null}
                      onClick={() => {
                        ponastaviGeslo(u);
                      }}
                    >
                      {ponastavljam === u.id ? 'Pošiljam …' : 'Ponastavi geslo'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="povezava"
                    onClick={() => {
                      koncajSeje(u);
                    }}
                  >
                    Končaj vse seje
                  </button>
                  <button
                    type="button"
                    className="povezava nevarna"
                    onClick={() => {
                      arhiviraj(u);
                    }}
                  >
                    Arhiviraj
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

/**
 * Izid po vabilu ali ponastavitvi gesla. Če pošta ne odide, skrbnik dobi
 * povezavo, ki jo posreduje sam; povezava pride s strežnika (`PORTAL_URL`).
 */
function IzidPovezave({ izid, vrsta }: { izid: IzidVabila; vrsta: Izid['vrsta'] }): JSX.Element {
  const povezava = celotnaPovezava(izid);
  // Ponastavitev velja eno uro, zato je pomembna tudi ura, ne le datum.
  const velja = vrsta === 'ponastavitev' ? datumInUra(izid.velja) : datum(izid.velja);
  const kaj = vrsta === 'ponastavitev' ? 'Povezava za novo geslo' : 'Vabilo';
  return (
    <div className={izid.posta === 'poslano' ? 'kartica uspeh' : 'kartica zavrnjeno'}>
      {izid.posta === 'poslano' && (
        <p className="uspeh-besedilo">
          {kaj} je poslan{vrsta === 'ponastavitev' ? 'a' : 'o'} na {izid.email}.
          {vrsta === 'ponastavitev' && ` Velja do ${velja}.`}
        </p>
      )}
      {izid.posta !== 'poslano' && (
        <>
          <p className="napaka">
            {izid.posta === 'ni-nastavljeno'
              ? 'E-pošta ni bila poslana, ker pošiljanje ni nastavljeno.'
              : 'E-pošta ni bila poslana.'}
          </p>
          {izid.napaka !== null && <p className="drobno">{izid.napaka}</p>}
          {povezava === null && izid.pot !== null && (
            <p className="drobno">
              Povezave ni mogoče sestaviti, ker naslov portala ni nastavljen
              (<code>PORTAL_URL</code>). {kaj} je izdan{vrsta === 'ponastavitev' ? 'a' : 'o'} in velja do {velja};
              pot do nje{vrsta === 'ponastavitev' ? '' : 'ga'} je <code>{izid.pot}</code>.
            </p>
          )}
          {povezava !== null && (
            <>
              <p className="drobno">
                Povezavo pošljite {vrsta === 'ponastavitev' ? izid.email : 'stranki'} sami. Velja do {velja}.
              </p>
              <p className="povezava-vabila">
                <code>{povezava}</code>
              </p>
              <button
                type="button"
                className="povezava"
                onClick={() => {
                  void navigator.clipboard?.writeText(povezava);
                }}
              >
                Kopiraj povezavo
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
